import { useCallback, useRef, useState } from 'react'

/**
 * Ids for rows that exist only in the grid. Seeded far below any real id and
 * decremented, so any number of drafts can be staged at once.
 */
const DRAFT_ID_START = -1_000_000

export const isDraftRowId = (id: number) => Number(id) <= DRAFT_ID_START

/**
 * Staged new rows for the sensors and valves lists.
 *
 * Both lists used to show exactly one blank row while editing, which capped
 * you at one addition per save and gave nothing to press if you wanted a
 * second. Drafts are added on demand instead, and each can be dropped without
 * abandoning the rest of the batch.
 */
const useDraftRows = () => {
  const [draftIds, setDraftIds] = useState<number[]>([])
  const nextIdRef = useRef(DRAFT_ID_START)

  const addDraft = useCallback(() => {
    const id = nextIdRef.current
    nextIdRef.current -= 1
    setDraftIds((prev) => [...prev, id])
    return id
  }, [])

  const removeDraft = useCallback(
    (id: number) => setDraftIds((prev) => prev.filter((entry) => entry !== id)),
    [],
  )

  const resetDrafts = useCallback(() => {
    setDraftIds([])
    nextIdRef.current = DRAFT_ID_START
  }, [])

  return { draftIds, addDraft, removeDraft, resetDrafts }
}

export default useDraftRows
